import {
  AnalyticsMetricDatapoint,
  AnalyticsSummaryStats,
  GraphType,
  GroupByColumn,
  isDecisionEngineAnalyticsMetricsDefinition,
  isDecisionEngineInteractionsMetricDefinition,
  isPredefinedAnalyticsMetricDefinition,
  isRelatedColumn,
  isSyntheticColumn,
  isTraitColumn,
  NormalizationType,
  NormalizedToRateMetadata,
  SyntheticColumn,
  TransformedColumn,
} from "@hightouch/lib/query/visual/types";
import {
  AudienceAggregationType,
  isEventMetricConfig,
  isModelMetricConfig,
  PerUserAggregationType,
  PredefinedMetric,
  predefinedMetricNames,
  SumMetricConfig,
} from "@hightouch/lib/query/visual/types/goals";
import { toZonedTime } from "date-fns-tz";
import { isEqualWith, uniqBy } from "lodash";
import set from "lodash/set";
import { format as formatNumber } from "numerable";
import pluralize from "pluralize";
import { UnreachableCaseError } from "ts-essentials";
import { isPresent } from "ts-extras";

import {
  getModelIdFromColumn,
  getOperatorLabel,
  getPropertyNameFromColumn,
  getPropertyNameFromProperty,
  getTraitNameFromColumn,
} from "src/components/explore/visual/utils";
import {
  AnalyticsCohortDefinitionOutput,
  MetricData,
  MetricError,
  MetricIdentifier,
  MetricJobIdentifier,
  MetricResultData,
} from "src/graphql";
import {
  EventOrMetricOption,
  ParentModel,
  SelectedAudience,
} from "src/pages/analytics/types";
import { AggregationOption } from "src/pages/metrics/constants";
import {
  mapAggregationConfigurationToConfigurationOption,
  mapNormalizationConfigurationToDescriptionLabel,
} from "src/pages/metrics/utils";
import {
  AndOrCondition,
  AnalyticsMetricDefinition,
  ConditionType,
  FilterableColumn,
  isColumnTypeValue,
  OperatorsWithoutValue,
  PropertyCondition,
  RawColumn,
  RelatedColumn,
  Relationship,
  TraitDefinition,
} from "src/types/visual";
import { accurateCommaNumber } from "src/utils/numbers";

import { graphBaseColors, graphColors } from "src/pages/analytics/constants";
import { DECISION_ENGINE_INTERACTIONS_METRIC_LABEL } from "src/pages/analytics/decision-engine-utils";
import { MetricResultMaybeFromCache } from "src/pages/analytics/hooks/use-metric-series";
import {
  findAudienceFromCohort,
  getColumnReferenceFromGroupByColumn,
  isGroupByColumnRelatedToModel,
  isGroupByColumnRelatedToParent,
  numberAndStringValidator,
} from "src/pages/analytics/utils";
import { GraphScale } from "./constants";
import { DataPoint, Graph, GroupColumn, SummaryData } from "./types";

export const transformMetricDataForGraph = ({
  audiences = [],
  graphType,
  cumulative = false,
  events = [],
  traits = [],
  relatedModels = [],
  groupByColumns = [],
  metricResults = [],
  metrics = [],
  parent,
  transformForPerformance = false,
  availableMetricsOptions,
}: {
  audiences: SelectedAudience[];
  graphType?: GraphType;
  cumulative?: boolean;
  events: Relationship[];
  relatedModels: Relationship[];
  traits: TraitDefinition[];
  groupByColumns: GroupByColumn[];
  metricResults: MetricResultMaybeFromCache[];
  metrics: { id: string; name: string }[];
  parent: ParentModel | null;
  transformForPerformance?: boolean;
  availableMetricsOptions?: EventOrMetricOption[];
}) => {
  const graph: Graph = { series: [], summary: [] };

  if (!metricResults.length) return graph;

  const metricNamesById: Record<string, string> = {};
  if (availableMetricsOptions) {
    availableMetricsOptions.forEach((option) => {
      metricNamesById[option.id] = option.name;
    });
  } else {
    metrics.forEach((metric) => {
      metricNamesById[metric.id] = metric.name;
    });
    events.forEach((event) => {
      if (event.id !== null) {
        metricNamesById[event.id] = event.to_model.name ?? "";
      }
    });
  }

  relatedModels.forEach((relatedModel) => {
    if (relatedModel.id !== null) {
      metricNamesById[relatedModel.id] = relatedModel.to_model.name ?? "";
    }
  });

  if (parent) metricNamesById[parent.id] = parent.name;

  // Use a different color for each series
  let colorIndex = 0;

  let dateRange: number[] = [];

  const ids = metricResults.map(({ ids }) => ids);

  const hasMultipleAudiences = audiences.length > 1;
  const hasMultipleCohorts = uniqueCohorts(ids).size > 1;

  metricResults.forEach(({ ids, result }, index) => {
    const audienceId = Number(
      ids.cohortDefinition?.parentModelId ?? ids.cohortId,
    );

    const foundAudience = findAudienceFromCohort(
      audiences,
      ids.cohortDefinition,
      audienceId,
    );

    const audienceName = foundAudience?.name ?? "";

    if ("data" in result) {
      const metricAndCohortCombinationKey = getMetricAndCohortCombinationKey(
        ids.cohortId,
        ids.metricId,
        ids.metricDefinition,
      );

      const metricNoun = getMetricNoun({
        metricDefinition: ids.metricDefinition,
        parent,
      });
      const metricName = getMetricName({
        ids,
        events,
        traits,
        relatedModels,
        metricNamesById,
        parent,
        options: {
          includeDescription: true,
        },
      });
      const rawMetricName = getMetricName({
        ids,
        events,
        traits,
        relatedModels,
        parent,
        metricNamesById,
      });

      const metricDefinition = ids.metricId
        ? metrics.find(({ id }) => id === ids.metricId)
        : ids.metricDefinition;

      if (!metricDefinition) {
        // No metric definition found, so don't add metric to graph.
        return;
      }

      // metricName can collide (same event, different aggregation/column/filter),
      // so row grouping keys off metricKey instead; it extends
      // metricAndCohortCombinationKey with the rest of the metric's identity.
      const metricFilterConditions =
        metricDefinition.config?.filter?.subconditions;
      const metricKey = `${metricAndCohortCombinationKey}:${JSON.stringify({
        aggregation: metricDefinition.aggregation,
        audienceAggregation: metricDefinition.audienceAggregation,
        normalization: metricDefinition.config?.normalization,
        column: metricDefinition.config?.column,
        filter: metricFilterConditions?.length
          ? metricFilterConditions
          : undefined,
      })}`;

      const { data, summaryStats } = result;

      // We'll only show summary stats if one audience is selected.
      // At the moment, it's difficult to compare audiences because of the variation between
      // audiences in split names, split %, presence of splits, etc.
      // The UI needs to be tweaked if we want to do that in the future.
      if (!hasMultipleCohorts && summaryStats && summaryStats.data.length) {
        graph.summary.push({
          metricKey,
          metricName,
          isSavedMetric: Boolean(ids.metricId),
          // Date values coming back from the backend are "in UTC" (reference metricSeriesData's calculatedAt below)
          timeWindow: {
            start: toZonedTime(
              summaryStats.timeWindow.start,
              "UTC",
            ).toISOString(),
            end: toZonedTime(summaryStats.timeWindow.end, "UTC").toISOString(),
          },
          data: transformSummaryStatsData(summaryStats.data, audienceName),
        });
      }

      const eventsByModelId = {};
      for (const e of events) {
        eventsByModelId[e.to_model.id] = e;
      }

      data.forEach((metricSeries) => {
        const relevantGroupByColumns = findGroupByColumnsForMetric(
          groupByColumns,
          eventsByModelId[metricDefinition?.config?.eventModelId?.toString()],
          parent,
        );

        const grouping = relevantGroupByColumns
          .map((groupByColumn) => {
            const { buckets, ...gbColumn } = groupByColumn;
            const modelId = getModelIdFromColumn(groupByColumn);
            const isParentModelId = parent && parent.id.toString() === modelId;

            const model = isParentModelId
              ? parent
              : events.find(
                  ({ to_model }) => modelId === to_model.id.toString(),
                )?.to_model;

            // GroupByColumn may be a trait, so we get the trait's name if it exists
            const traitName = getTraitNameFromColumn(groupByColumn, traits);
            const columnNameOrAlias =
              traitName || getColumnName(groupByColumn, model);

            const groupBy = metricSeries.groupBy?.find((gb) =>
              isEqualWith(gbColumn, gb.column, numberAndStringValidator),
            );

            // Returned match
            if (groupBy) {
              return { ...groupBy, alias: columnNameOrAlias };
            }

            return {
              column: groupByColumn,
              alias: columnNameOrAlias,
              value: null,
            };
          })
          .filter(isPresent);

        const aggregationConfiguration = getAggregationConfiguration(
          metricDefinition as AnalyticsMetricDefinition,
        );

        const aggregation =
          mapAggregationConfigurationToConfigurationOption(
            aggregationConfiguration,
          ) ?? AggregationOption.Count;

        const clientSideCumulative =
          aggregationConfiguration.audienceAggregation !==
            AudienceAggregationType.Cumulative && cumulative;

        const normalization = metricDefinition?.config?.normalization;

        const seriesKey = getSeriesKey({
          metricAndCohortCombinationKey,
          audienceId,
          audienceName,
          metricName,
          splitId: metricSeries.splitId,
          groupByColumns: grouping,
          index,
        });

        const seriesDescription = getSeriesDescription({
          audienceName: hasMultipleAudiences ? audienceName : undefined,
          ...(transformForPerformance
            ? {
                metricName,
                splitName: metricSeries.splitId,
                groupByColumns: grouping,
              }
            : {}),
        });

        const legendName = getLegendName({
          audienceName,
          metricName,
          splitName: metricSeries.splitId,
          groupByColumns: grouping,
          hasMultipleAudiences,
        });

        const data: DataPoint[] = [];
        let currentSum = 0;

        const isLiveAudienceSize =
          metricDefinition.predefinedMetric === PredefinedMetric.AudienceSize &&
          !result.audienceSnapshotUsed;
        const valueTooltipDescription = isLiveAudienceSize
          ? "Only showing the current audience size. Historical data requires snapshots, which are either disabled in your source settings or unavailable for this time range."
          : undefined;

        const dashedStyle =
          isLiveAudienceSize || isModelMetricConfig(metricDefinition.config);

        metricSeries.data.forEach(({ timestamp, value, meta }) => {
          // We know the date values coming back from the backend are "in UTC"
          // (meaning they have no time zone, and represent the date we care
          // about in UTC). So, we convert them so they represent the same
          // timestamp, but in the browser's local time zone instead. That way,
          // when we call any date format methods, they'll show the date
          // correctly (since the browser renders dates in the local time zone).
          const calculatedAt = toZonedTime(timestamp, "UTC").getTime();
          dateRange.push(calculatedAt);

          const metadata = isNormalizedToRateMeta(meta) ? meta : undefined;
          const errorBounds = metadata
            ? {
                lowerBound: metadata.lowerConfidenceBound,
                upperBound: metadata.upperConfidenceBound,
              }
            : undefined;

          data.push({
            calculatedAt,
            // biome-ignore lint/suspicious/noAssignInExpressions: intentional
            metricValue: clientSideCumulative ? (currentSum += value) : value,
            seriesKey,

            // TODO(samuel): pass in custom cohort id in version 2.0
            audienceId: ids.cohortId?.toString() ?? "",
            aggregation,
            metricName,
            splitId: metricSeries.splitId,
            grouping,
            normalization,
            metadata,
            errorBounds,
          });
        });

        // Get the color and bg from the palette entry, depending on chart type.
        const normalizePaletteEntry = (entry: {
          color: string;
          bg?: string;
        }) => ({
          color: entry.color,
          bg: entry.bg ?? entry.color,
        });

        // Since area charts need to leverage both base and light colors for each series item, it must use graphBaseColors.
        const palette =
          graphType === GraphType.Area ? graphBaseColors : graphColors;

        const { color, bg } = normalizePaletteEntry(
          palette[colorIndex++ % palette.length] as {
            color: string;
            bg?: string;
          },
        );

        graph.series.push({
          key: seriesKey,
          metricKey,
          metricId: ids.metricId ?? ids.metricDefinition?.id ?? "",
          metricName,
          rawMetricName,
          metricNoun,
          description: seriesDescription,
          legendName,
          audienceName,
          splitName: metricSeries.splitId ?? undefined,
          grouping,
          aggregation,
          color: color,
          lightColor: graphType === GraphType.Area ? bg : undefined,
          style: dashedStyle ? "dashed" : "solid",
          normalization,
          data,
          metricFilterConditions: metricFilterConditions ?? [],
          metricColumnReference:
            metricDefinition.config?.column &&
            getColumnReferenceFromGroupByColumn(
              metricDefinition.config?.column,
            ),

          valueTooltipDescription,
        });
      });
    }
  });

  dateRange = dateRange.filter((date, index, self) => {
    return self.findIndex((d) => d === date) === index;
  });
  dateRange.sort((dateA, dateB) => (dateA > dateB ? 1 : -1));

  // fill in missing values for performance graph
  if (transformForPerformance) {
    graph.series = graph.series.map((series) => {
      return {
        ...series,
        data: fillInMissingDateValues(series.key, series.data, dateRange),
      };
    });
  }

  return graph;
};

const isNormalizedToRateMeta = (
  meta: AnalyticsMetricDatapoint["meta"],
): meta is NormalizedToRateMetadata => {
  return (
    meta !== undefined &&
    "lowerConfidenceBound" in meta &&
    "upperConfidenceBound" in meta &&
    "numerator" in meta &&
    "denominator" in meta
  );
};

const getMetricAndCohortCombinationKey = (
  cohortId: string | null,
  metricId: string | null,
  metricDefinition: AnalyticsMetricDefinition,
) => {
  let metricAndCohortCombinationKey: string = cohortId?.toString() ?? "";
  // There are three kinds of metrics
  // 1) "saved" metrics -- in which case we have the ID
  // 2) "predefined" metrics -- in which case we use the predefinedMetric ID
  // 3) "live" metric -- an event model that we use to query, so we use the event model ID
  // They are all different types, so we have to build the unique key conditionally
  if (metricId) {
    metricAndCohortCombinationKey += metricId.toString();
  } else if (isPredefinedAnalyticsMetricDefinition(metricDefinition)) {
    metricAndCohortCombinationKey +=
      metricDefinition.predefinedMetric.toString();
  } else if (isDecisionEngineAnalyticsMetricsDefinition(metricDefinition)) {
    metricAndCohortCombinationKey += metricDefinition.flowId;
  } else if (
    metricDefinition?.config &&
    isEventMetricConfig(metricDefinition.config)
  ) {
    metricAndCohortCombinationKey +=
      metricDefinition.config.eventModelId.toString();
  } else if (
    metricDefinition?.config &&
    isModelMetricConfig(metricDefinition.config)
  ) {
    metricAndCohortCombinationKey += metricDefinition.config.modelId.toString();
  }

  return metricAndCohortCombinationKey;
};

export const valueFormatter = (
  value: number,
  format: GraphScale = GraphScale.Linear,
): string => {
  if (format === GraphScale.Linear) {
    // If value is less than 1 but not 0, show more decimal places
    return value < 1 && value > -1 && value !== 0
      ? formatNumber(value, "0.0000")
      : formatNumber(value, "0,0[.]0a");
  } else {
    return formatNumber(value, "0.##%");
  }
};

const getColumnName = (
  column: RawColumn | RelatedColumn | SyntheticColumn | TransformedColumn,
  model?: { filterable_audience_columns: FilterableColumn[] } | undefined,
) => {
  const columnName = getPropertyNameFromColumn(column) ?? "";

  if (!model) {
    return columnName;
  }

  const columnDefinition = model.filterable_audience_columns.find(
    ({ name }) => name === columnName,
  );

  return columnDefinition?.alias ?? columnDefinition?.name ?? columnName;
};

const getSeriesKey = ({
  audienceId,
  audienceName,
  groupByColumns = [],
  index,
  metricAndCohortCombinationKey,
  metricName,
  splitId,
}: {
  // We can have multiple cohort definitions with the same ID but different
  // filters (i.e. parent model vs ad hod audience)
  audienceId: number;
  audienceName: string;
  groupByColumns?: GroupColumn[] | null;
  index?: number;
  metricAndCohortCombinationKey: string;
  metricName: string;
  splitId?: string | null;
}): string => {
  return [
    index,
    metricAndCohortCombinationKey,
    audienceId,
    audienceName,
    metricName,
    splitId,
    ...(groupByColumns ?? []).map(
      ({ column, alias, value }) =>
        `${alias ?? getPropertyNameFromColumn(column)} ${value}`,
    ),
  ]
    .filter(isPresent)
    .join(":");
};

export const getSeriesDescription = ({
  audienceName,
  splitName,
  groupByColumns = [],
}: {
  audienceName?: string;
  splitName?: string | null;
  groupByColumns?: GroupColumn[] | null;
} = {}): string => {
  return [
    audienceName !== "" ? audienceName : undefined,
    splitName,
    ...(groupByColumns ?? []).map(
      ({ column, alias, value }) =>
        `${alias ?? getPropertyNameFromColumn(column)} = ${value}`,
    ),
  ]
    .filter(isPresent)
    .join(" / ");
};

/**
 * This function is used to get the legend name for the graph
 *
 * The first section (the metric name) is always  there, but the other sections
 *  only appear when needed to disambiguate series
 * -> If there's only 1 segment, don't include that
 * -> If there's only 1 split group, don't include that
 * -> if there's no normalization, no need to include that (should already be
 *  included in metric name)
 */
export const getLegendName = ({
  audienceName,
  metricName,
  splitName,
  groupByColumns = [],
  hasMultipleAudiences,
}: {
  audienceName?: string;
  metricName: string;
  splitName?: string | null;
  groupByColumns?: GroupColumn[];
  hasMultipleAudiences: boolean;
}) => {
  const description = getSeriesDescription({
    audienceName: hasMultipleAudiences ? audienceName : undefined,
    splitName,
    groupByColumns,
  });

  return [metricName, description].filter(Boolean).join(" / ");
};

export const getMetricName = ({
  ids,
  events,
  traits,
  relatedModels,
  parent,
  metricNamesById,
  options,
}: {
  ids: Omit<MetricIdentifier, "__typename">;
  events: Relationship[];
  traits: TraitDefinition[];
  relatedModels: Relationship[];
  parent: ParentModel | null;
  metricNamesById: Record<string, string>;
  options?: {
    includeDescription: boolean;
  };
}): string => {
  // Saved metric references `metricId`
  if (ids.metricId) {
    return metricNamesById[ids.metricId] ?? "Metric";
  }

  // Live metric - predefined or ad-hoc
  const metricDefinition = ids.metricDefinition as AnalyticsMetricDefinition;

  // Predefined metric
  if (isPredefinedAnalyticsMetricDefinition(metricDefinition)) {
    return getPredefinedMetricName(
      metricDefinition.predefinedMetric as PredefinedMetric,
    );
  }

  const metricDefinitionName = isDecisionEngineInteractionsMetricDefinition(
    metricDefinition,
  )
    ? DECISION_ENGINE_INTERACTIONS_METRIC_LABEL
    : (metricNamesById[metricDefinition.id ?? ""] ??
      // Fallback to relationshipId if no id
      (metricDefinition.config.relationshipId &&
        metricNamesById[metricDefinition.config.relationshipId]));

  // Ad-hoc metric
  const metricName = metricDefinitionName ?? "Event";

  if (!options?.includeDescription) {
    return metricName;
  }

  const aggregation =
    mapAggregationConfigurationToConfigurationOption(metricDefinition);

  const normalization = isDecisionEngineAnalyticsMetricsDefinition(
    metricDefinition,
  )
    ? mapNormalizationConfigurationToDescriptionLabel(metricDefinition)
    : null;

  if (!aggregation && !normalization) {
    return metricName;
  }

  const config = metricDefinition.config as SumMetricConfig;

  let columns: FilterableColumn[] | undefined;

  if (isModelMetricConfig(config)) {
    if (
      config.relationshipId &&
      config.relationshipId !== parent?.id?.toString()
    ) {
      columns = relatedModels.find(
        ({ id }) =>
          ids.metricDefinition?.config?.relationshipId?.toString() ===
          id.toString(),
      )?.to_model.filterable_audience_columns;
    } else {
      columns = parent?.filterable_audience_columns;
    }
  } else {
    columns = events.find(
      ({ id }) =>
        ids.metricDefinition?.config?.relationshipId?.toString() ===
        id.toString(),
    )?.to_model.filterable_audience_columns;
  }

  const column =
    getMetricDescriptionColumnForTrait({ config, traits }) ??
    columns?.find(
      ({ name }) => name === getPropertyNameFromColumn(config.column),
    );

  const metricDescription = aggregation
    ? `[${getMetricDescription({
        aggregationMethod: aggregation,
        column,
        isModelMetricConfig: isModelMetricConfig(metricDefinition.config),
      })}]`
    : "";

  const normalizationDescription = normalization ? `[${normalization}]` : "";

  return [metricName, metricDescription, normalizationDescription]
    .filter(Boolean)
    .join(" ");
};

const getMetricNoun = ({
  metricDefinition,
  parent,
}: {
  metricDefinition?: AnalyticsMetricDefinition;
  parent: ParentModel | null;
}): "event" | "row" | "user" => {
  if (!metricDefinition) return "event";

  if (
    isPredefinedAnalyticsMetricDefinition(metricDefinition) &&
    metricDefinition.predefinedMetric === PredefinedMetric.AudienceSize
  ) {
    return "user";
  }

  if (
    "config" in metricDefinition &&
    isModelMetricConfig(metricDefinition.config)
  ) {
    if (metricDefinition.config.modelId === parent?.id?.toString()) {
      return "user";
    }

    return "row";
  }

  return "event";
};

const getMetricDescriptionColumnForTrait = ({
  config,
  traits,
}: {
  config: SumMetricConfig;
  traits: TraitDefinition[];
}): MetricDescriptionColumn | undefined => {
  const column = config.column;
  if (!isRelatedColumn(column)) return undefined;

  const traitColumn = column.column;
  if (!isTraitColumn(traitColumn)) return undefined;

  const trait = traits.find(
    (trait) => trait.id === traitColumn.traitDefinitionId,
  );
  if (!trait) return undefined;

  return {
    alias: null,
    name: trait.name,
  };
};

type MetricDescriptionColumn = {
  alias: string | null;
  name: string | null;
};

export const getMetricDescription = ({
  aggregationMethod,
  column,
  isModelMetricConfig = false,
}: {
  aggregationMethod: AggregationOption;
  column: MetricDescriptionColumn | undefined;
  isModelMetricConfig?: boolean;
}) => {
  const aggregation = aggregationMethod;
  const columnName = column?.alias ?? column?.name;
  const objectName = isModelMetricConfig ? "rows" : "events";

  switch (aggregation) {
    case AggregationOption.Count:
      return `Total ${objectName}`;
    case AggregationOption.UniqueUsers:
      return "Unique members";
    case AggregationOption.PercentOfAudience:
      return "% of audience";
    case AggregationOption.CountDistinctProperty:
      return `Distinct count of ${columnName}`;
    case AggregationOption.AverageOfProperty:
      return `Average of ${columnName}`;
    case AggregationOption.AverageOfPropertyPerUser:
      return `Average of ${columnName} per member`;
    case AggregationOption.SumOfProperty:
      return `Sum of ${columnName}`;
    case AggregationOption.SumOfPropertyPerUser:
      return `Sum of ${columnName} per member - Averaged`;
    default:
      throw new UnreachableCaseError(aggregation);
  }
};

const getPredefinedMetricName = (metricEnum: PredefinedMetric) => {
  return predefinedMetricNames[metricEnum];
};

// Best-effort text summary of a metric's filter conditions, so metrics that
// share a display name can still be told apart. Omits anything it can't
// confidently describe rather than showing something misleading.
export const getMetricFilterSummary = (
  conditions: AndOrCondition<PropertyCondition>[] | undefined,
): string | undefined => {
  if (!conditions?.length) return undefined;

  const described = conditions.map(describeCondition).filter(isPresent);
  if (!described.length) return undefined;

  // A lone top-level group needs no parens to disambiguate against.
  const parts =
    described.length === 1
      ? [described[0]?.text ?? ""]
      : described.map((child) => parenthesizeIfMixed(child, "and"));

  return `Where ${parts.join(" and ")}`;
};

type DescribedCondition = {
  text: string;
  // Set only for a multi-child group, so a sibling joined under a different
  // operator knows to parenthesize it.
  joiner?: "and" | "or";
};

const parenthesizeIfMixed = (
  condition: DescribedCondition,
  parentJoiner: "and" | "or",
): string =>
  condition.joiner && condition.joiner !== parentJoiner
    ? `(${condition.text})`
    : condition.text;

const describeCondition = (
  condition: AndOrCondition<PropertyCondition>,
): DescribedCondition | undefined => {
  if (
    condition.type !== ConditionType.And &&
    condition.type !== ConditionType.Or
  ) {
    const text = describePropertyCondition(condition);
    return text ? { text } : undefined;
  }

  const joiner = condition.type === ConditionType.And ? "and" : "or";
  const children = condition.conditions
    .map(describeCondition)
    .filter(isPresent);

  if (!children.length) return undefined;
  if (children.length === 1) return children[0];

  const text = children
    .map((child) => parenthesizeIfMixed(child, joiner))
    .join(` ${joiner} `);

  return { text, joiner };
};

const describePropertyCondition = (
  condition: PropertyCondition,
): string | undefined => {
  const propertyName = getPropertyNameFromProperty(condition.property);
  if (!propertyName) return undefined;

  const operatorLabel =
    getOperatorLabel(condition.operator, condition.propertyType) ??
    condition.operator;

  if (OperatorsWithoutValue.includes(condition.operator)) {
    return `${propertyName} ${operatorLabel}`;
  }

  const value = stringifyConditionValue(condition.value);
  return value ? `${propertyName} ${operatorLabel} ${value}` : propertyName;
};

const stringifyConditionValue = (value: unknown): string | undefined => {
  if (value == null) return undefined;

  if (isColumnTypeValue(value)) {
    return getPropertyNameFromProperty(value.property) ?? undefined;
  }

  if (Array.isArray(value)) {
    if (!value.length) return undefined;
    const items = value.map(String);
    return items.length > 2
      ? `${items.slice(0, 2).join(", ")}, +${items.length - 2} more`
      : items.join(", ");
  }

  if (["string", "number", "boolean"].includes(typeof value)) {
    return String(value);
  }

  return undefined;
};

export const fillInMissingDateValues = (
  seriesKey: string,
  data: DataPoint[],
  dateArray: number[],
): DataPoint[] => {
  const result: DataPoint[] = [];

  const dataByDate = data.reduce((all, dataPoint) => {
    all[dataPoint.calculatedAt] = dataPoint;

    return all;
  }, {});

  dateArray.map((date) => {
    const entry = dataByDate[date];
    if (entry) {
      result.push(entry);
    } else {
      const { metricName, audienceId, splitId, grouping, aggregation } =
        entry ?? {};

      result.push({
        calculatedAt: date,
        metricValue: 0,
        seriesKey,

        aggregation,
        metricName,
        audienceId,
        splitId,
        grouping,
      });
    }
  });

  return result;
};

export const getTooltipSuffixTextFromMetric = ({
  noun,
  aggregation,
  normalization,
}: {
  noun: "event" | "row" | "user";
  aggregation: AggregationOption;
  normalization: NormalizationType | undefined;
}) => {
  if (normalization === NormalizationType.LiftPercent) {
    return "";
  }

  let aggregationSuffix = "";

  switch (aggregation) {
    case AggregationOption.Count:
      aggregationSuffix = pluralize(noun);
      break;
    case AggregationOption.UniqueUsers:
      aggregationSuffix = "members";
      break;
    case AggregationOption.AverageOfPropertyPerUser:
    case AggregationOption.SumOfPropertyPerUser:
      aggregationSuffix = "per member";
      break;
  }

  let normalizationSuffix = "";
  switch (normalization) {
    case NormalizationType.RatePerInteraction:
      normalizationSuffix = "per interaction";
      break;
    case NormalizationType.RatePerUser: {
      if (aggregation !== AggregationOption.UniqueUsers) {
        normalizationSuffix = "per member";
      }
      break;
    }
  }

  return [aggregationSuffix, normalizationSuffix].filter(Boolean).join(" ");
};

export const getSummaryFromAggregation = ({
  aggregation,
  modelName,
  columnName,
  isTrait,
}: {
  aggregation: AggregationOption;
  modelName: string;
  columnName?: string;
  isTrait?: boolean;
}) => {
  if (isTrait)
    return getTraitSummaryFromAggregation({
      aggregation,
      traitName: columnName ?? "trait",
    });

  switch (aggregation) {
    case AggregationOption.Count:
      return `Total number of "${modelName}" events performed`;
    case AggregationOption.CountDistinctProperty:
      return `Total number of distinct "${columnName}" across all events`;
    case AggregationOption.UniqueUsers:
      return `Total number of members who performed "${modelName}"`;
    case AggregationOption.PercentOfAudience:
      return `% of members who performed "${modelName}"`;
    case AggregationOption.SumOfProperty:
      return `SUM of "${columnName}" of "${modelName}" events`;
    case AggregationOption.SumOfPropertyPerUser:
      return `SUM of "${columnName}" per member of "${modelName}" events`;
    case AggregationOption.AverageOfProperty:
      return `Average of "${columnName}" across all "${modelName}" events`;
    case AggregationOption.AverageOfPropertyPerUser:
      return `Average of "${columnName}" per member of "${modelName}" events`;
    default:
      throw new UnreachableCaseError(aggregation);
  }
};

const getTraitSummaryFromAggregation = ({
  aggregation,
  traitName,
}: {
  aggregation: AggregationOption;
  traitName: string;
}) => {
  switch (aggregation) {
    // Traits are inherently unique users, because they are
    // represented as a row on the user model
    case AggregationOption.Count:
    case AggregationOption.UniqueUsers:
      return `Total number of members with ${traitName}`;
    case AggregationOption.CountDistinctProperty:
      return `Total number of distinct "${traitName}" across all members`;
    case AggregationOption.PercentOfAudience:
      return `% of members with ${traitName}`;
    case AggregationOption.SumOfProperty:
    case AggregationOption.SumOfPropertyPerUser:
      return `SUM of "${traitName}"`;
    case AggregationOption.AverageOfProperty:
    case AggregationOption.AverageOfPropertyPerUser:
      return `Average of "${traitName}" across all members`;
    default:
      throw new UnreachableCaseError(aggregation);
  }
};

export const formatMetricValue = (
  metricValue: number,
  isPercentage = false,
) => {
  const value = isPercentage ? metricValue * 100 : metricValue;

  return isPercentage
    ? `${value < 1 ? value.toFixed(4) : value.toFixed(2)}%`
    : accurateCommaNumber(value < 1 ? value.toFixed(4) : value.toFixed(2));
};

const getAggregationConfiguration = (
  metricDefinition: AnalyticsMetricDefinition,
): {
  aggregation: PerUserAggregationType;
  audienceAggregation: AudienceAggregationType;
} => {
  if (isPredefinedAnalyticsMetricDefinition(metricDefinition)) {
    const predefinedMetric =
      metricDefinition.predefinedMetric as PredefinedMetric;

    if (predefinedMetric === PredefinedMetric.AudienceSize) {
      return {
        aggregation: PerUserAggregationType.UniqueUsers,
        audienceAggregation: AudienceAggregationType.Sum,
      };
    }

    if (predefinedMetric === PredefinedMetric.UserEvents) {
      return {
        aggregation: PerUserAggregationType.Count,
        audienceAggregation: AudienceAggregationType.Sum,
      };
    }

    throw new Error(`Unrecognized predefined metric: ${predefinedMetric}`);
  } else {
    return {
      aggregation: metricDefinition.aggregation,
      audienceAggregation: metricDefinition.audienceAggregation,
    };
  }
};

const transformSummaryStatsData = (
  data: AnalyticsSummaryStats[],
  audienceName: string | undefined,
): SummaryData[] => {
  const hasSplits = data.length > 1;

  const baselineValue =
    (hasSplits ? data.find((s) => s.isBaseline)?.value : data[0]?.value) ?? 0;

  // Compute the percent difference for each split
  const transformedSummaryStats = data.map((summary) => {
    // Existing lift
    const percentDifference =
      baselineValue === 0 ? 0 : (summary.value - baselineValue) / baselineValue;

    // New lift, if it's not available we'll fall back to the percent difference
    const lift = {
      value: summary.lift?.value ?? percentDifference,
      confidenceInterval: summary.lift?.confidenceInterval ?? null,
    };

    return {
      ...summary,
      percentDifference,
      lift,
      audienceName,
    };
  });

  // If there are splits, find the winning split group so we can highlight it
  if (hasSplits) {
    let largestIndex = -1;
    let largestValue = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < data.length; i++) {
      const current = data[i];
      if (current == null) {
        // This shouldn't be null, but do this to appease typescript
        continue;
      }

      if (current.value > largestValue) {
        largestValue = current.value;
        largestIndex = i;
      }
    }
    set(transformedSummaryStats, [largestIndex, "isWinner"], true);
  }

  return transformedSummaryStats;
};

const uniqueCohorts = (
  metrics: (MetricIdentifier | MetricJobIdentifier)[],
): Set<string | AnalyticsCohortDefinitionOutput> => {
  const cohorts = new Set<string | AnalyticsCohortDefinitionOutput>();

  for (const { cohortId, cohortDefinition } of metrics) {
    // Each metric is assigned to a cohort consisting of either a cohortId or cohortDefinition
    // so we check for both

    if (cohortId && !cohorts.has(cohortId)) {
      cohorts.add(cohortId);
    }

    if (cohortDefinition && !cohorts.has(cohortDefinition)) {
      cohorts.add(cohortDefinition);
    }
  }

  return cohorts;
};

/**
 * Determine which groupByColumns belongs to the specific metric result's data
 * We need to do this because we may have multiple groupByColumns across different
 * event models since we allow groupBys of the same column names when viewing
 * multiple metrics in the charts. When mapping the data, we need to make sure
 * we map the groupByColumn that belongs to that specific metric so we don't show
 * duplicates. Example:
 * selectedMetrics: [Metric1, Metric2]
 * groupByColumns: [Metric1.source, Metric2.source]
 * -> want to map Metric1.source groupByColumn to Metric1 and etc...
 */
const findGroupByColumnsForMetric = (
  groupByColumns: GroupByColumn[],
  metricEvent: Relationship | undefined,
  parent: ParentModel | null,
): GroupByColumn[] => {
  // For metric definitions without an event model, return all parent groupByColumns
  // and unique event groupByColumns by name since we are only going to display
  // one column in the graph for an event column name
  const metricEventModelId = metricEvent?.to_model?.id;
  if (!metricEventModelId) {
    const parentGroupByColumns = groupByColumns.filter((gb) =>
      isGroupByColumnRelatedToParent(parent, gb),
    );

    const uniqueEventGroupByColumns = uniqBy(
      groupByColumns.filter((gb) => {
        return !isGroupByColumnRelatedToParent(parent, gb);
      }),
      (gb) => getPropertyNameFromColumn(gb),
    );

    return parentGroupByColumns.concat(uniqueEventGroupByColumns);
  }

  return groupByColumns.filter((gb) => {
    return Boolean(
      isGroupByColumnRelatedToParent(parent, gb) ||
        isGroupByColumnRelatedToModel(metricEvent, gb) ||
        // Synthetic columns are not directly tied to a specific event model
        // so always show these
        isSyntheticColumn(gb),
    );
  });
};

export function isMetricData(data: MetricResultData): data is MetricData {
  return "data" in data;
}

export function isMetricError(data: MetricResultData): data is MetricError {
  return "error" in data;
}
